/**
 * Test Pricing — sandbox for suggested price (real engine via API).
 */

import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { testSuggestedPrice } from "../services/rateCardsService.js";

const { Text, Paragraph } = Typography;

/**
 * @param {{ open: boolean, onClose: () => void }} props
 */
export default function TestPricingDrawer({ open, onClose }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showTrace, setShowTrace] = useState(false);

  const handleCalculate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setResult(null);
      const data = await testSuggestedPrice({
        originProvince: values.originProvince,
        destinationProvince: values.destinationProvince,
        originCity: values.originCity,
        destinationCity: values.destinationCity,
        service: values.service || "shared_load",
        billableVolumeM3: values.billableVolumeM3,
        routeKm: values.routeKm,
        access: {
          stairs: Boolean(values.stairs),
          longCarry: Boolean(values.longCarry),
          difficultVehicleAccess: Boolean(values.difficultVehicleAccess),
          noLoadingZone: Boolean(values.noLoadingZone),
        },
      });
      setResult(data);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.error || error.message || "Calculate failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Test Pricing"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Paragraph type="secondary">
        Decision-support sandbox. Uses the live rate card and pricing engines —
        does not create or change a customer quote.
      </Paragraph>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          originProvince: "GP",
          destinationProvince: "WC",
          service: "shared_load",
          billableVolumeM3: 8.5,
        }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="originProvince"
              label="Origin province"
              rules={[{ required: true }]}
            >
              <Input placeholder="GP" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="destinationProvince"
              label="Destination province"
              rules={[{ required: true }]}
            >
              <Input placeholder="WC" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="originCity" label="Origin city (optional)">
              <Input placeholder="Johannesburg" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="destinationCity" label="Destination city (optional)">
              <Input placeholder="Cape Town" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="service" label="Service">
              <Select
                options={[
                  { value: "shared_load", label: "Shared Load" },
                  { value: "exclusive_load", label: "Exclusive Load" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="billableVolumeM3"
              label="Billable volume (m³)"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="routeKm" label="Route km (optional)">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Space wrap style={{ marginBottom: 16 }}>
          <Form.Item name="stairs" valuePropName="checked" noStyle>
            <Checkbox>Stairs</Checkbox>
          </Form.Item>
          <Form.Item name="longCarry" valuePropName="checked" noStyle>
            <Checkbox>Long carry</Checkbox>
          </Form.Item>
          <Form.Item name="difficultVehicleAccess" valuePropName="checked" noStyle>
            <Checkbox>Difficult access</Checkbox>
          </Form.Item>
          <Form.Item name="noLoadingZone" valuePropName="checked" noStyle>
            <Checkbox>No loading zone</Checkbox>
          </Form.Item>
        </Space>

        <Button
          type="primary"
          onClick={handleCalculate}
          loading={loading}
          data-testid="test-calculate-suggested-price"
        >
          Calculate Suggested Price
        </Button>
      </Form>

      {result && (
        <div style={{ marginTop: 24 }} data-testid="test-suggested-price-result">
          <Alert
            type={result.suggestedTotal != null ? "success" : "warning"}
            showIcon
            message={
              result.suggestedTotal != null
                ? `Suggested price: R ${Number(result.suggestedTotal).toLocaleString("en-ZA")}`
                : "No suggested price"
            }
            description={result.compactSummary}
          />
          {result.matchedRow && (
            <Paragraph style={{ marginTop: 12 }}>
              Matched row: <Text strong>{result.matchedRow.id}</Text>
              {` · R${result.matchedRow.ratePerM3}/m³ · min R${result.matchedRow.minimumAmount}`}
            </Paragraph>
          )}
          <Button type="link" onClick={() => setShowTrace((v) => !v)} style={{ paddingLeft: 0 }}>
            {showTrace ? "Hide calculation details" : "View calculation details"}
          </Button>
          {showTrace && (
            <pre
              style={{
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 8,
                fontSize: 12,
                overflow: "auto",
              }}
            >
              {JSON.stringify(result.trace, null, 2)}
            </pre>
          )}
        </div>
      )}
    </Drawer>
  );
}
